import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import * as LucideIcons from "lucide-react";
import FallbackImg from "../../../components/FallbackImg";
import Policy from "../../../assets/aftersalesservice_icons/policy.svg";
import Download from "../../../assets/aftersalesservice_icons/warranty_download.svg";
import BackBlue from "../../../assets/aftersalesservice_icons/back_blue_icon.svg";
import { useLanguage } from "../LanguageContext";
import ApiService from "../../../components/Api_services";
import PreviewLayout from "./PreviewLayout";

const lightenColor = (hex, amount = 0.5) => {
  if (!hex) return null;

  const clean = hex.replace("#", "");
  const normalized =
    clean.length === 3
      ? clean
          .split("")
          .map((c) => c + c)
          .join("")
      : clean;
  const value = parseInt(normalized, 16);
  if (Number.isNaN(value) || normalized.length !== 6) return hex;

  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  const lighten = (channel) => Math.round(channel + (255 - channel) * amount);

  return `rgb(${lighten(r)}, ${lighten(g)}, ${lighten(b)})`;
};

const SectionIcon = ({ name, color, size = 20 }) => {
  const IconComp = name && LucideIcons[name];
  return IconComp ? (
    <IconComp
      color={color || undefined}
      size={size}
      style={{ flexShrink: 0 }}
    />
  ) : null;
};

const PreviewWarrantyPolicy = () => {
  const { trans, lang } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const itemId = searchParams.get("item_id");
  const country = searchParams.get("country") || "";
  const [warrantyPolicy, setWarrantyPolicy] = useState(null);
  const [warrantyPolicyPdf, setWarrantyPolicyPdf] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const sections = useMemo(() => {
    const item = (warrantyPolicy || [])[0];
    return (item?.sections || [])
      .slice()
      .sort((a, b) => (Number(a.seq_no) || 0) - (Number(b.seq_no) || 0));
  }, [warrantyPolicy]);

  const openPdf = () => {
    window.open(warrantyPolicyPdf, "_blank");
  };

  useEffect(() => {
    const fetchWarrantyPolicy = async () => {
      const apiLang = lang === "ch" ? "cn" : lang === "my" ? "ms" : lang;
      setLoaded(false);

      try {
        const response = await ApiService.getWarrantyPolicy(
          itemId,
          apiLang,
          country,
        );
        if (response && response.status === 200) {
          setWarrantyPolicy(response.data);
          setWarrantyPolicyPdf(response.data[0]?.pdf_file);
        } else {
          setWarrantyPolicy(null);
          setWarrantyPolicyPdf(null);
          console.error("Error fetching warranty policy:", response.message);
        }
      } catch (error) {
        setWarrantyPolicy(null);
        setWarrantyPolicyPdf(null);
        console.error("Error fetching warranty policy:", error);
      } finally {
        setLoaded(true);
      }
    };

    fetchWarrantyPolicy();
  }, [itemId, country, lang]);

  // shown once the fetch has finished (success or error) with no sections
  const emptySections = loaded && sections.length === 0 && (
    <div className="as-user-manual-item" style={{ backgroundColor: "#ffffff" }}>
      <div className="as-no-results" style={{ display: "block" }}>
        <div>{trans.no_warranty_policy}</div>
      </div>
    </div>
  );

  return (
    <PreviewLayout>
      <div className="as-warranty-policy-content" id="warranty_policy_modal">
        <div className="as-warranty-policy-container">
          <div style={{ padding: "0 31.97px" }}>
            <div className="as-warranty-policy-card">
              <button
                type="button"
                className="as-back-btn"
                id="warranty_policy_back"
                onClick={() =>
                  navigate(`/aftersalesservice/preview?item_id=${itemId}&country=${country}`)
                }
              >
                <FallbackImg src={BackBlue} alt="back_blue_icon" />
                {trans.back}
              </button>

              <div className="as-card-header">
                <FallbackImg
                  src={Policy}
                  className="as-warranty_icon"
                  alt="policy"
                />

                <div className="as-text">
                  <h2>{trans.warr_policy}</h2>
                  <p className="as-warranty-policy-subtitle">
                    {trans.warr_policy_sub}
                  </p>
                </div>
              </div>

              <button
                className="as-download-btn"
                type="button"
                onClick={openPdf}
              >
                <FallbackImg
                  src={Download}
                  className="as-download_icon"
                  alt="download"
                />
                {trans.download_policy}
              </button>
            </div>

            {sections.map((section) => (
              <div
                className="as-user-manual-item"
                key={section.id}
                style={{
                  backgroundColor: section.background_color
                    ? section.background_color
                    : "#ffffff",
                  color: section.text_color ? section.text_color : "#000000",
                }}
              >
                <div className="as-user-manual-title">
                  <div
                    className="as-user-manual-title-icon"
                    style={{
                      backgroundColor: section.background_color
                        ? lightenColor(section.background_color)
                        : "#f0f0f0",
                    }}
                  >
                    <SectionIcon
                      name={section.icon}
                      color={section.text_color}
                    />
                  </div>
                  <div>{section.description}</div>
                </div>
                {(section.children || []).map((child) => (
                  <div
                    className="as-user-manual-box"
                    key={child.id}
                    style={{
                      backgroundColor: child.background_color
                        ? child.background_color
                        : "transparent",
                      color: child.text_color ? child.text_color : "#000000",
                    }}
                  >
                    <SectionIcon name={child.icon} color={child.text_color} />
                    <div>{child.description}</div>
                  </div>
                ))}
              </div>
            ))}
            {emptySections}

            <div className="as-contact-card">
              <p>
                {trans.ques_warr}
                <br />
                {trans.our_sup_1}
              </p>

              <button className="as-contact-btn" type="button">
                {trans.contact_sup}
              </button>
            </div>
          </div>
        </div>
      </div>
    </PreviewLayout>
  );
};

export default PreviewWarrantyPolicy;
